/**
 * /admin/communities/$communityId — full-page settings for one custom
 * community (replaces the old edit modal).
 *
 * The identity hero at the top IS the live preview: it re-renders from the
 * unsaved form state as you type, and a Save/Reset pair appears in the sticky
 * header while the profile is dirty. Everything below the profile applies
 * immediately through its own RPC (tier grants, people, roles, channels) —
 * only the profile fields wait for Save. Archive/unarchive live in the danger
 * zone; communities are never deleted.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Check,
  Loader2,
  RotateCcw,
  Users,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useArchiveCustomCommunity,
  useCommunityMembers,
  useCommunityTierGrants,
  useCommunityTiersForHub,
  useCustomCommunities,
  useCustomCommunitiesForHub,
  useGrantCommunityTierAccess,
  useRevokeCommunityTierAccess,
  useUnarchiveCustomCommunity,
  useUpdateCustomCommunity,
} from '@/api/community-hooks';
import type { CommunityHubCard, CustomCommunityRow } from '@/api/community';
import { getChannelIcon } from '@/lib/channel-icons';
import LucideIconPicker from '@/components/community/LucideIconPicker';
import {
  ColorSwatchRow,
  COMMUNITY_COLORS,
  TierRow,
} from '@/components/community/community-form';
import {
  ChannelsSection,
  MembersSection,
  QuickLinksSection,
  Section,
} from '@/components/community/CommunityManageSections';

export const Route = createFileRoute('/admin/communities_/$communityId')({
  component: CommunitySettingsPage,
});

function CommunitySettingsPage() {
  const { communityId } = Route.useParams();

  const { data: active = [], isLoading: activeLoading } = useCustomCommunities();
  const { data: archived = [], isLoading: archivedLoading } = useCustomCommunities({
    archived: true,
  });
  const loading = activeLoading || archivedLoading;
  const community =
    active.find((c) => c.id === communityId) ??
    archived.find((c) => c.id === communityId) ??
    null;

  if (loading && !community) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  if (!community) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <p className="text-sm text-muted-foreground">Community not found.</p>
        <Button variant="outline" size="sm" asChild>
          <Link to="/admin/communities">
            <ArrowLeft className="w-4 h-4" aria-hidden />
            Back to communities
          </Link>
        </Button>
      </div>
    );
  }

  return <CommunitySettings key={community.id} community={community} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Settings body — keyed by community id so form state re-seeds on navigation
// ─────────────────────────────────────────────────────────────────────────────

function CommunitySettings({ community }: { community: CustomCommunityRow }) {
  const isArchived = community.archived_at !== null;

  // Profile form state (applies on Save; renaming retitles the chat too).
  const [name, setName] = useState(community.name);
  const [description, setDescription] = useState(community.description ?? '');
  const [icon, setIcon] = useState<string | null>(community.icon ?? null);
  const [color, setColor] = useState<string>(community.color ?? COMMUNITY_COLORS[0]);

  // Re-seed when a save lands and the row refetches.
  useEffect(() => {
    setName(community.name);
    setDescription(community.description ?? '');
    setIcon(community.icon ?? null);
    setColor(community.color ?? COMMUNITY_COLORS[0]);
  }, [community.name, community.description, community.icon, community.color]);

  const dirty =
    name.trim() !== community.name ||
    (description.trim() || null) !== (community.description ?? null) ||
    (icon ?? null) !== (community.icon ?? null) ||
    color !== (community.color ?? COMMUNITY_COLORS[0]);

  const update = useUpdateCustomCommunity();

  const resetProfile = () => {
    setName(community.name);
    setDescription(community.description ?? '');
    setIcon(community.icon ?? null);
    setColor(community.color ?? COMMUNITY_COLORS[0]);
  };

  const saveProfile = () => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      toast.error('Community name is required');
      return;
    }
    update.mutate(
      {
        communityId: community.id,
        name: trimmed,
        description: description.trim() || null,
        icon: icon ?? null,
        color,
      },
      {
        onSuccess: () => toast.success(`"${trimmed}" updated`),
        onError: (err) => toast.error(err?.message ?? 'Failed to update community'),
      },
    );
  };

  // Hero data: exact member count from the roster; post count from the hub
  // card when available (the hub RPC skips archived communities).
  const { data: members = [] } = useCommunityMembers(community.id);
  const { data: hubCards = [] } = useCustomCommunitiesForHub();
  const postCount = hubCards.find((c) => c.id === community.id)?.post_count;

  const HeroIcon = getChannelIcon(icon)?.Component ?? Users;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Sticky header: back link + save actions while the profile is dirty */}
      <div className="flex items-center justify-between gap-3 px-4 sm:px-6 h-14 border-b border-border flex-shrink-0 bg-background/95 backdrop-blur">
        <Link
          to="/admin/communities"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors min-w-0"
        >
          <ArrowLeft className="w-4 h-4 flex-shrink-0" aria-hidden />
          <span className="truncate">Communities</span>
        </Link>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isArchived && (
            <span className="rounded-full border border-border px-2.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              Archived
            </span>
          )}
          {dirty && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetProfile}
                disabled={update.isPending}
              >
                <RotateCcw className="w-3.5 h-3.5" aria-hidden />
                Reset
              </Button>
              <Button size="sm" onClick={saveProfile} disabled={update.isPending || !name.trim()}>
                {update.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                ) : (
                  <Check className="w-4 h-4" aria-hidden />
                )}
                Save changes
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-4 sm:p-6 max-w-3xl space-y-5">
          {/* Identity hero — the live preview. Renders from UNSAVED form state
              so edits below are reflected here before Save. */}
          <div className="flex items-center gap-4">
            <span
              className={`flex w-14 h-14 items-center justify-center rounded-xl text-white flex-shrink-0 ${isArchived ? 'opacity-60' : ''}`}
              style={{ backgroundColor: color }}
            >
              <HeroIcon className="w-7 h-7" aria-hidden />
            </span>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold truncate">
                {name.trim() || community.name}
              </h1>
              <p className="text-sm text-muted-foreground">
                {members.length} member{members.length === 1 ? '' : 's'}
                {typeof postCount === 'number'
                  ? ` · ${postCount} post${postCount === 1 ? '' : 's'}`
                  : ''}
              </p>
            </div>
          </div>

          {/* Profile */}
          <Section
            label="Profile"
            hint="Name, look, and description. Applied on Save — renaming updates the chat header too."
          >
            <div className="space-y-4">
              <div className="flex items-end gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="community-name">Name</Label>
                  <Input
                    id="community-name"
                    value={name}
                    maxLength={100}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setName(e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Icon</Label>
                  <LucideIconPicker value={icon} onChange={setIcon} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Accent color</Label>
                <ColorSwatchRow value={color} onChange={setColor} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="community-description">Description</Label>
                <Textarea
                  id="community-description"
                  value={description}
                  maxLength={500}
                  rows={2}
                  placeholder="What this community is about (optional)"
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setDescription(e.target.value)
                  }
                />
              </div>
            </div>
          </Section>

          <TierAccessSection communityId={community.id} />

          <MembersSection communityId={community.id} members={members} />

          <ChannelsSection communityId={community.id} />

          <QuickLinksSection communityId={community.id} communityName={community.name} />

          <DangerZone community={community} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tier access — grants/revokes apply immediately, revoke asks first
// ─────────────────────────────────────────────────────────────────────────────

function TierAccessSection({ communityId }: { communityId: string }) {
  const { data: tiers = [] } = useCommunityTiersForHub();
  const { data: grants = [] } = useCommunityTierGrants(communityId);
  const grantMutation = useGrantCommunityTierAccess();
  const revokeMutation = useRevokeCommunityTierAccess();
  const [revokeTarget, setRevokeTarget] = useState<CommunityHubCard | null>(null);

  const grantedTierIds = useMemo(
    () => new Set(grants.map((g) => g.community_tier_id)),
    [grants],
  );

  const toggleTier = (tier: CommunityHubCard) => {
    if (grantedTierIds.has(tier.id)) {
      setRevokeTarget(tier);
      return;
    }
    grantMutation.mutate(
      { communityId, tierId: tier.id },
      {
        onSuccess: (added) => {
          toast.success(
            `${tier.name} granted — ${added} member${added === 1 ? '' : 's'} added`,
          );
        },
        onError: (err) => toast.error(err?.message ?? 'Failed to grant tier access'),
      },
    );
  };

  const confirmRevoke = () => {
    if (!revokeTarget) return;
    revokeMutation.mutate(
      { communityId, tierId: revokeTarget.id },
      {
        onSuccess: (removed) => {
          toast.success(
            `${revokeTarget.name} revoked — ${removed} member${removed === 1 ? '' : 's'} removed`,
          );
          setRevokeTarget(null);
        },
        onError: (err) => toast.error(err?.message ?? 'Failed to revoke tier access'),
      },
    );
  };

  return (
    <Section
      label="Tier access"
      hint="Members of a granted tier join automatically, now and in the future. Grants and revokes apply immediately."
    >
      <div className="rounded-lg border border-border divide-y divide-border overflow-hidden">
        {tiers.map((tier) => (
          <TierRow
            key={tier.id}
            tier={tier}
            checked={grantedTierIds.has(tier.id)}
            onToggle={() => toggleTier(tier)}
          />
        ))}
        {tiers.length === 0 && (
          <p className="p-4 text-center text-xs text-muted-foreground">No tiers available</p>
        )}
      </div>

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(next) => !next && setRevokeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke {revokeTarget?.name} access?</AlertDialogTitle>
            <AlertDialogDescription>
              Members who joined only through {revokeTarget?.name} lose access to
              this community. Hand-picked members, community admins, and members
              covered by another granted tier keep their access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRevoke} disabled={revokeMutation.isPending}>
              {revokeMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
              ) : (
                <X className="w-4 h-4" aria-hidden />
              )}
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Danger zone — archive / unarchive with confirm
// ─────────────────────────────────────────────────────────────────────────────

function DangerZone({ community }: { community: CustomCommunityRow }) {
  const isArchived = community.archived_at !== null;
  const archiveMutation = useArchiveCustomCommunity();
  const unarchiveMutation = useUnarchiveCustomCommunity();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pending = archiveMutation.isPending || unarchiveMutation.isPending;

  const confirm = () => {
    const mutation = isArchived ? unarchiveMutation : archiveMutation;
    mutation.mutate(
      { communityId: community.id },
      {
        onSuccess: () => {
          toast.success(
            isArchived ? `"${community.name}" restored` : `"${community.name}" archived`,
          );
          setConfirmOpen(false);
        },
        onError: (err) =>
          toast.error(
            err?.message ?? `Failed to ${isArchived ? 'unarchive' : 'archive'} community`,
          ),
      },
    );
  };

  return (
    <section className="space-y-2.5">
      <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
        Danger zone
      </p>
      <div className="rounded-xl border border-destructive/30 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium">
            {isArchived ? 'Unarchive this community' : 'Archive this community'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isArchived
              ? 'The community, its channels, posts, and chat become visible to its members again.'
              : 'Hides the community, its channels, posts, and chat from every member. Everything is preserved.'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className={
            isArchived
              ? 'flex-shrink-0'
              : 'flex-shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive'
          }
          onClick={() => setConfirmOpen(true)}
        >
          {isArchived ? (
            <ArchiveRestore className="w-4 h-4" aria-hidden />
          ) : (
            <Archive className="w-4 h-4" aria-hidden />
          )}
          {isArchived ? 'Unarchive' : 'Archive'}
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isArchived ? 'Unarchive' : 'Archive'} "{community.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isArchived
                ? 'The community, its channels, posts, and chat become visible to its members again.'
                : 'The community, its channels, posts, and chat will be hidden from every member. Everything is preserved — unarchiving restores full visibility.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirm} disabled={pending}>
              {pending ? (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
              ) : isArchived ? (
                <ArchiveRestore className="w-4 h-4" aria-hidden />
              ) : (
                <Archive className="w-4 h-4" aria-hidden />
              )}
              {isArchived ? 'Unarchive' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
